import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

interface ProblemType {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  is_blinking: boolean;
  created_at?: string;
  updated_at?: string;
}

const ProblemTypes: React.FC = () => {
  const { t } = useLanguage();
  const [problemTypes, setProblemTypes] = useState<ProblemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProblemType, setEditingProblemType] = useState<ProblemType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_blinking: false,
  });

  useEffect(() => {
    fetchProblemTypes();
  }, []);

  const fetchProblemTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/problem-types');
      setProblemTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching problem types:', error);
      alert(t('problemTypes.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProblemType(null);
    setFormData({ name: '', description: '', is_active: true, is_blinking: false });
    setShowModal(true);
  };

  const handleEdit = (problemType: ProblemType) => {
    setEditingProblemType(problemType);
    setFormData({
      name: problemType.name,
      description: problemType.description || '',
      is_active: problemType.is_active,
      is_blinking: !!problemType.is_blinking,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('problemTypes.confirmDelete'))) return;

    try {
      await api.delete(`/problem-types/${id}`);
      fetchProblemTypes();
      alert(t('problemTypes.deleted'));
    } catch (error: any) {
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('problemTypes.couldNotDelete')));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert(t('problemTypes.nameRequired'));
      return;
    }

    try {
      if (editingProblemType) {
        await api.put(`/problem-types/${editingProblemType.id}`, formData);
        alert(t('problemTypes.updated'));
      } else {
        await api.post('/problem-types', formData);
        alert(t('problemTypes.created'));
      }
      setShowModal(false);
      fetchProblemTypes();
    } catch (error: any) {
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('problemTypes.couldNotSave')));
    }
  };

  const toggleActive = async (problemType: ProblemType) => {
    try {
      await api.put(`/problem-types/${problemType.id}`, {
        ...problemType,
        is_active: !problemType.is_active,
      });
      fetchProblemTypes();
    } catch (error: any) {
      alert(t('common.error') + ': ' + (error.response?.data?.message || t('problemTypes.couldNotUpdate')));
    }
  };

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 20 }}>
        <h1
          style={{
            color: '#2d3748',
            background: 'none',
            WebkitBackgroundClip: 'initial',
            WebkitTextFillColor: '#2d3748',
            backgroundClip: 'initial',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ WebkitTextFillColor: 'initial' }}>🔧</span>
          {t('problemTypes.title')}
        </h1>
        <button
          onClick={handleCreate}
          className="btn"
          style={{ backgroundColor: '#667eea', color: '#ffffff', fontWeight: 600 }}
        >
          + {t('problemTypes.addNew')}
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('problemTypes.name')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('problemTypes.description')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('problemTypes.blinking')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('problemTypes.status')}
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('problemTypes.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {problemTypes.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        padding: '48px 20px',
                        textAlign: 'center',
                        color: '#718096',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('problemTypes.empty')}
                    </td>
                  </tr>
                )}
                {problemTypes.map((problemType) => (
                  <tr
                    key={problemType.id}
                    style={{
                      borderBottom: '1px solid #e1e8ed',
                      backgroundColor: problemType.is_active ? 'white' : '#f8f9fa',
                    }}
                  >
                    <td style={{ padding: '14px 12px', fontWeight: 600, color: '#2d3748' }}>
                      {problemType.name}
                    </td>
                    <td style={{ padding: '14px 12px', color: '#4a5568' }}>
                      {problemType.description || '—'}
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'center', fontSize: 18 }}>
                      {problemType.is_blinking ? '⚠️' : '—'}
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          backgroundColor: problemType.is_active ? '#d1fae5' : '#fee2e2',
                          color: problemType.is_active ? '#065f46' : '#991b1b',
                        }}
                      >
                        {problemType.is_active ? t('problemTypes.active') : t('problemTypes.inactive')}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEdit(problemType)}
                          className="btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: 13,
                            backgroundColor: 'white',
                            color: '#667eea',
                            border: '2px solid #667eea',
                          }}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => toggleActive(problemType)}
                          className="btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: 13,
                            backgroundColor: problemType.is_active ? '#f59e0b' : '#10b981',
                            color: 'white',
                          }}
                        >
                          {problemType.is_active ? t('problemTypes.deactivate') : t('problemTypes.activate')}
                        </button>
                        <button
                          onClick={() => handleDelete(problemType.id)}
                          className="btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: 13,
                            backgroundColor: '#ef4444',
                            color: 'white',
                          }}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 30,
              borderRadius: 12,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {editingProblemType ? t('problemTypes.editTitle') : t('problemTypes.addTitle')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('problemTypes.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('problemTypes.namePlaceholder')}
                  required
                  className="input"
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('problemTypes.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('problemTypes.descriptionPlaceholder')}
                  rows={4}
                  className="input"
                  style={{ resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span style={{ color: '#4a5568' }}>{t('problemTypes.activeLabel')}</span>
                </label>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_blinking}
                    onChange={(e) => setFormData({ ...formData, is_blinking: e.target.checked })}
                  />
                  <span style={{ color: '#4a5568' }}>{t('problemTypes.blinkingLabel')}</span>
                </label>
                <div style={{ marginTop: 4, marginLeft: 24, fontSize: 12, color: '#718096' }}>
                  {t('problemTypes.blinkingHelp')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{ backgroundColor: '#667eea', color: 'white' }}
                >
                  {editingProblemType ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemTypes;
